import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionMenu,
  ConfirmationDialog,
  DataGrid,
  Dialog,
  FilterToolbar,
  Form,
  formatDate,
  FormButton,
  Input,
  SelectInput,
  StatusBadge,
  string,
  SwitchInput,
  useNotifications,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  GetAdminLoginsParams,
  useDeleteAdminLogin,
  useGetAdminLogins,
  usePatchAdminLoginResetPassword,
  usePostAdminLogin,
  usePostAdminLoginSendInvitation
} from '@ijoin/hooks';
import { MoreVert } from '@mui/icons-material';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { camelCase } from 'lodash';
import { SyntheticEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';
import { z } from 'zod';

import {
  LinkButton,
  ResetLinkDialog,
  ResetPasswordDialog
} from '../../components';
import { ACCOUNT_TYPES } from '../../consts';
import { getLoginType } from '../../helpers';

type Login = {
  AccountsRefLogins?: { accountTypeId: string; isAdmin?: boolean }[];
  allowAccessAfter?: string;
  createdAt: string;
  email?: string;
  failedAttempts?: number;
  firstName?: string;
  lastName?: string;
  loginId: string;
  use2fa?: boolean;
  username: string;
};

type CreateFormProps = {
  email: string;
  firstName: string;
  isAdmin: boolean;
  lastName: string;
  use2fa: boolean;
  username: string;
  userType: 'CONTROL_PANEL' | 'METRICS_DASHBOARD';
};

const createDefaultValues: CreateFormProps = {
  email: '',
  firstName: '',
  isAdmin: false,
  lastName: '',
  use2fa: false,
  username: '',
  userType: 'CONTROL_PANEL'
};

const userTypeOptions = [
  { label: 'Control Panel', value: 'CONTROL_PANEL' },
  { label: 'Metrics Dashboard', value: 'METRICS_DASHBOARD' }
];

const UsersPage = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const [query, setQuery] = useState<GridQuery>();
  const [accountTypeId, setAccountTypeId] = useState(ACCOUNT_TYPES.ADMIN);
  const [createDialogOpen, toggleCreateDialogOpen, setCreateDialogOpen] =
    useToggle(false);
  const [selectedUser, setSelectedUser] = useState<Login | null>(null);
  const [deleteLoginOpen, , setDeleteLoginOpen] = useToggle(false);
  const [resetPasswordOpen, , setResetPasswordOpen] = useToggle(false);
  const [resetEmailOpen, , setResetEmailOpen] = useToggle(false);
  const [resetLinkOpen, , setResetLinkOpen] = useToggle(false);
  const deleteConfirmMethods = useForm({ defaultValues: { confirmText: '' } });

  const filters: FilterDef[] = [
    { label: 'Search', name: 'search', type: 'text' }
  ];

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [`/api/v3/admin/logins`] });

  const isAdminView = accountTypeId === ACCOUNT_TYPES.ADMIN;

  const logins = useGetAdminLogins(
    {
      ...query,
      accountTypeId,
      limit: query?.take,
      offset: query?.skip
    } as GetAdminLoginsParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const createMethods = useForm<CreateFormProps>({
    defaultValues: createDefaultValues,
    mode: 'all',
    resolver: zodResolver(
      z.object({
        email: z.string().email(),
        firstName: string({ required: true }),
        isAdmin: z.boolean(),
        lastName: string({ required: true }),
        use2fa: z.boolean(),
        username: string({ required: true }),
        userType: z.enum(['CONTROL_PANEL', 'METRICS_DASHBOARD'])
      })
    )
  });

  const postLogin = usePostAdminLogin({
    mutation: {
      onError: () => notifications.error('Failed to create user'),
      onSuccess: () => {
        invalidate();
        setCreateDialogOpen(false);
        createMethods.reset(createDefaultValues);
        notifications.success('User created and invitation sent');
      }
    }
  });

  const deleteLogin = useDeleteAdminLogin({
    mutation: {
      onError: () => notifications.error('Failed to delete login'),
      onSuccess: () => {
        invalidate();
        notifications.success('Login deleted');
      }
    }
  });

  const resetPasswordEmail = usePatchAdminLoginResetPassword({
    mutation: {
      onError: () => notifications.error('Failed to send password reset email'),
      onSuccess: () => notifications.success('Password reset email sent')
    }
  });

  const sendInvitation = usePostAdminLoginSendInvitation({
    mutation: {
      onError: () => notifications.error('Failed to send invitation'),
      onSuccess: () => notifications.success('Invitation sent')
    }
  });

  const onConfirmDeleteLogin = () => {
    if (!selectedUser) return;

    deleteLogin.mutate({ loginId: selectedUser.loginId });
    setDeleteLoginOpen(false);
    setSelectedUser(null);
    deleteConfirmMethods.reset();
  };

  const onDeleteLoginClose = () => {
    setDeleteLoginOpen(false);
    setSelectedUser(null);
    deleteConfirmMethods.reset();
  };

  const onResetPasswordClose = () => {
    setResetPasswordOpen(false);
    setSelectedUser(null);
  };

  const onResetEmailClose = () => {
    setResetEmailOpen(false);
    setSelectedUser(null);
  };

  const onConfirmResetEmail = () => {
    if (!selectedUser) return;

    resetPasswordEmail.mutate({
      data: { mode: 'email' as const },
      loginId: selectedUser.loginId
    });
    setResetEmailOpen(false);
    setSelectedUser(null);
  };

  const onResetLinkClose = () => {
    setResetLinkOpen(false);
    setSelectedUser(null);
  };

  const columns: GridColDef<Login>[] = [
    {
      field: 'username',
      flex: 2,
      headerName: 'Username',
      renderCell: params => (
        <LinkButton
          data-testid={camelCase(params.row.username)}
          params={{ loginId: params.row.loginId }}
          to='/users/$loginId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'firstName',
      flex: 1,
      headerName: 'First Name'
    },
    {
      field: 'lastName',
      flex: 1,
      headerName: 'Last Name'
    },
    {
      field: 'email',
      flex: 2,
      headerName: 'Email'
    },
    {
      field: 'superAdmin',
      flex: 0.75,
      headerName: 'Super Admin',
      renderCell: params => {
        const adminRef = params.row.AccountsRefLogins?.find(
          a => a.accountTypeId === ACCOUNT_TYPES.ADMIN
        );

        return (
          <StatusBadge severity={adminRef?.isAdmin ? 'success' : 'info'}>
            {adminRef?.isAdmin ? 'Yes' : 'No'}
          </StatusBadge>
        );
      },
      sortable: false
    },
    {
      field: 'use2fa',
      flex: 0.75,
      headerName: '2FA',
      renderCell: params => (
        <StatusBadge severity={params.value ? 'success' : 'warning'}>
          {params.value ? 'Enabled' : 'Disabled'}
        </StatusBadge>
      ),
      sortable: false
    },
    {
      field: 'createdAt',
      flex: 1,
      headerName: 'Created',
      valueFormatter: value =>
        value ? formatDate(value, { format: 'MM/DD/YYYY' }) : ''
    },
    {
      field: 'locked',
      flex: 0.75,
      headerName: 'Status',
      renderCell: params =>
        (params.row.failedAttempts ?? 0) >= 5 ||
        !!params.row.allowAccessAfter ? (
          <StatusBadge severity='error'>Locked</StatusBadge>
        ) : (
          <StatusBadge severity='success'>Active</StatusBadge>
        ),
      sortable: false
    },
    {
      field: 'actions',
      flex: 0.5,
      headerName: 'Actions',
      renderCell: params => (
        <ActionMenu
          actions={[
            {
              content: 'Delete Login',
              onSelect: () => {
                setSelectedUser(params.row);
                setDeleteLoginOpen(true);
              }
            },
            getLoginType(params.row.AccountsRefLogins) === 'advisor' && {
              content: 'Generate Reset Link',
              onSelect: () => {
                setSelectedUser(params.row);
                setResetLinkOpen(true);
              }
            },
            isAdminView && {
              content: 'Resend Invitation',
              onSelect: () =>
                sendInvitation.mutate({ loginId: params.row.loginId })
            },
            getLoginType(params.row.AccountsRefLogins) === 'admin' && {
              content: 'Reset Password',
              onSelect: () => {
                setSelectedUser(params.row);
                setResetPasswordOpen(true);
              }
            },
            getLoginType(params.row.AccountsRefLogins) === 'advisor' && {
              content: 'Send Reset Email',
              onSelect: () => {
                setSelectedUser(params.row);
                setResetEmailOpen(true);
              }
            }
          ].filter(Boolean)}
          trigger={
            <IconButton
              data-testid={camelCase(`actions ${params.row.username}`)}>
              <MoreVert />
            </IconButton>
          }
        />
      ),
      sortable: false
    }
  ];

  const onCreateSuperAdminChange = (isAdmin: boolean) => {
    if (isAdmin) {
      createMethods.setValue('use2fa', true);
    }
  };

  const onCreateSubmit = (data: CreateFormProps) => {
    postLogin.mutate({ data });
  };

  const onOpenCreateDialog = () => {
    createMethods.reset(createDefaultValues);
    setCreateDialogOpen(true);
  };

  const onAccountTypeChange = (_event: SyntheticEvent, value: string) => {
    setAccountTypeId(value);
    setQuery(current => current && { ...current, skip: 0 });
  };

  const isCreateSuperAdmin = createMethods.watch('isAdmin');

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Typography variant='h5'>Users</Typography>

      <Tabs onChange={onAccountTypeChange} value={accountTypeId}>
        <Tab
          data-testid='tabAdmins'
          label='Admins'
          value={ACCOUNT_TYPES.ADMIN}
        />
        <Tab
          data-testid='tabAdvisors'
          label='Advisors'
          value={ACCOUNT_TYPES.ADVISORS}
        />
        <Tab
          data-testid='tabParticipants'
          label='Participants'
          value={ACCOUNT_TYPES.PARTICIPANTS}
        />
      </Tabs>

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filters}
        getRowId={row => row.loginId}
        initialSort={[{ field: 'username', sort: 'asc' }]}
        loading={!query || logins.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        rowCount={logins.data?.pagination?.total ?? 0}
        rows={logins.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'users'
          },
          toolbar: {
            ...(isAdminView && {
              addLabel: 'User',
              addTestId: 'createUser',
              onAdd: onOpenCreateDialog
            }),
            onRefresh: logins.refetch,
            searchButtonTestId: 'search',
            triggerTestId: 'usersFilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />

      <Dialog
        data-testid='createUserDialog'
        fullWidth
        isOpen={createDialogOpen}
        maxWidth='sm'
        onClose={toggleCreateDialogOpen}>
        <Form
          methods={createMethods}
          noValidate
          onSubmit={createMethods.handleSubmit(onCreateSubmit)}>
          <DialogTitle variant='h3'>Create Admin User</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Input
                data-1p-ignore
                data-testid='createUsername'
                format='string'
                fullWidth
                hideError
                label='Username'
                name='username'
                required
                size='small'
              />
              <Input
                data-1p-ignore
                data-testid='createFirstName'
                format='string'
                fullWidth
                hideError
                label='First Name'
                name='firstName'
                required
                size='small'
              />
              <Input
                data-1p-ignore
                data-testid='createLastName'
                format='string'
                fullWidth
                hideError
                label='Last Name'
                name='lastName'
                required
                size='small'
              />
              <Input
                data-1p-ignore
                data-testid='createEmail'
                format='string'
                fullWidth
                label='Email'
                name='email'
                required
                size='small'
              />
              <SelectInput
                data-testid='createUserType'
                hideError
                label='User Type'
                name='userType'
                options={userTypeOptions}
                size='small'
                variant='outlined'
              />
              <SwitchInput
                color='success'
                data-testid='createIsAdmin'
                hideError
                label='Super Admin'
                name='isAdmin'
                onChange={onCreateSuperAdminChange}
              />
              <SwitchInput
                color='success'
                data-testid='create2FA'
                disabled={isCreateSuperAdmin}
                hideError
                label='2FA Enabled'
                name='use2fa'
                tooltip={
                  isCreateSuperAdmin ? 'Required for super admins.' : undefined
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelCreate'
              onClick={toggleCreateDialogOpen}
              variant='outlined'>
              Cancel
            </Button>
            <FormButton
              data-testid='saveCreate'
              triggerValidation
              variant='contained'>
              Create & Send Invitation
            </FormButton>
          </DialogActions>
        </Form>
      </Dialog>

      <Dialog
        data-testid='deleteLoginDialog'
        isOpen={deleteLoginOpen}
        onClose={onDeleteLoginClose}>
        <Form methods={deleteConfirmMethods}>
          <DialogTitle variant='h3'>Delete Login</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Type &quot;{selectedUser?.username}&quot; to confirm deleting this
              login and all linked accounts.
            </Typography>
            <Input
              autoComplete='off'
              data-1p-ignore
              data-testid='deleteConfirmInput'
              format='string'
              fullWidth
              label={`Type '${selectedUser?.username ?? ''}' to confirm`}
              name='confirmText'
              size='small'
            />
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelDeleteLogin'
              onClick={onDeleteLoginClose}
              variant='outlined'>
              Cancel
            </Button>
            <Button
              color='error'
              data-testid='confirmDeleteLogin'
              disabled={
                deleteConfirmMethods.watch('confirmText') !==
                selectedUser?.username
              }
              onClick={onConfirmDeleteLogin}
              variant='contained'>
              Delete
            </Button>
          </DialogActions>
        </Form>
      </Dialog>

      {selectedUser && (
        <>
          <ResetPasswordDialog
            isOpen={resetPasswordOpen}
            loginId={selectedUser.loginId}
            onClose={onResetPasswordClose}
            username={selectedUser.username}
          />

          <ConfirmationDialog
            confirmText='Send Email'
            data-testid='resetEmailDialog'
            isOpen={resetEmailOpen}
            message='Send password reset email to this advisor?'
            onCancel={onResetEmailClose}
            onConfirm={onConfirmResetEmail}
            title='Reset Password'
          />

          {resetLinkOpen && (
            <ResetLinkDialog
              isOpen={resetLinkOpen}
              loginId={selectedUser.loginId}
              onClose={onResetLinkClose}
              username={selectedUser.username}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export const Route = createFileRoute('/users/')({
  beforeLoad: () => {
    if (localStorage.getItem('isAdmin') !== '1') {
      throw redirect({ to: '/organizations' });
    }
  },
  component: UsersPage
});
